/**
 * Keyboard + pointer-lock mouse input, sampled by the fixed-step simulation.
 * Mouse deltas accumulate between sim ticks and are consumed once per tick so
 * sensitivity is framerate-independent.
 */

/** True when `el` is a text-entry element that owns the keyboard. Duck-typed
 *  (no instanceof) so headless harnesses can exercise the gate without a DOM. */
export function isTypingTarget(el: unknown): boolean {
  if (!el || typeof el !== "object") return false;
  const node = el as { tagName?: string; isContentEditable?: boolean };
  return node.tagName === "INPUT" || node.tagName === "TEXTAREA" || node.isContentEditable === true;
}

export class Input {
  private keys = new Set<string>();
  private pressed = new Set<string>();
  private released = new Set<string>();
  private buttons = new Set<number>();
  private pressedButtons = new Set<number>();
  private mouseDX = 0;
  private mouseDY = 0;
  pointerLocked = false;

  constructor(private canvas: HTMLCanvasElement) {
    canvas.addEventListener("mousedown", (e) => {
      this.buttons.add(e.button);
      this.pressedButtons.add(e.button);
    });
    window.addEventListener("mouseup", (e) => this.buttons.delete(e.button));
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    window.addEventListener("keydown", (e) => {
      // TYPING GATE: while a text field owns the keyboard (the live-gameplay
      // chat input), the sim must see nothing — not even a wasPressed edge.
      // Typing "f" must never disembark. Panel hotkeys are separately
      // swallowed capture-phase by the focused UI; this gate covers sampling.
      if (this.typing) return;
      // Browser/editor chords must not also trigger movement or X dive. Clear
      // held gameplay keys when a modifier arrives so cut/copy never leaves a
      // crouch, flight or movement edge pending beneath the native shortcut.
      if (e.ctrlKey || e.metaKey) {
        this.keys.clear(); this.pressed.clear(); this.released.clear();
        return;
      }
      // Space activates the last-focused button by default. While gameplay owns
      // the keyboard, suppress that browser behavior without losing the input.
      if (this.pointerLocked && e.code === "Space") e.preventDefault();
      if (e.repeat) return;
      this.keys.add(e.code);
      this.pressed.add(e.code);
    });
    window.addEventListener("keyup", (e) => {if(this.keys.delete(e.code))this.released.add(e.code);});
    window.addEventListener("blur", () => this.reset());
    // A text field taking focus flushes everything held — the matching keyups
    // will be swallowed by the gate, so a W held into the chat box must not
    // keep the avatar walking underneath the typing.
    window.addEventListener("focusin", (e) => {
      if (isTypingTarget(e.target)) {
        this.keys.clear();
        this.pressed.clear();
        this.released.clear();
      }
    });

    canvas.addEventListener("mousemove", (e) => {
      if (!this.pointerLocked) return;
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    });
    document.addEventListener("pointerlockchange", () => {
      this.pointerLocked = document.pointerLockElement === canvas;
      if (this.pointerLocked) {
        this.clearUiFocus();
      } else {
        this.keys.clear();
        this.pressed.clear();
        this.released.clear();
        this.buttons.clear();
        this.pressedButtons.clear();
      }
    });
  }

  /** True while a text-entry element owns the keyboard (chat input focused):
   *  the keydown gate is active and gameplay sampling sees nothing. */
  get typing(): boolean {
    return isTypingTarget(document.activeElement);
  }

  /** Drop held controls and pending edges when a modal takes or releases ownership. */
  reset(): void {
    this.keys.clear(); this.pressed.clear(); this.released.clear();
    this.buttons.clear(); this.pressedButtons.clear();
    this.mouseDX = 0; this.mouseDY = 0;
  }

  /** Mouse button held (0 = left/weapon 1, 2 = right/weapon 2). */
  isButtonDown(button: number): boolean {
    return this.buttons.has(button);
  }

  private lockSuppressedUntil = 0;

  /** Inverted flight/aim pitch (ESC-menu toggle, persisted by main). */
  invertY = false;

  /** No-op all lock requests for `ms` — cursor UIs opening from handlers
   *  that ALSO re-lock (panel onClose, wheel keyup) race the editor's
   *  exit otherwise, and the cursor vanishes until the user hits Esc. */
  suppressLock(ms: number): void {
    this.lockSuppressedUntil = performance.now() + ms;
  }

  requestPointerLock(): void {
    this.clearUiFocus();
    if (this.pointerLocked) return;
    if (performance.now() < this.lockSuppressedUntil) return;
    // Returns a promise in modern browsers; rejection (headless, iframe
    // policies) just means mouse steering is unavailable, not fatal.
    const result = this.canvas.requestPointerLock() as unknown;
    (result as Promise<void> | undefined)?.catch?.(() => {});
  }

  private clearUiFocus(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== this.canvas) active.blur();
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  /** True if the key went down since the last endTick(). */
  wasPressed(code: string): boolean {
    return this.pressed.has(code);
  }
  /** A real keyup edge, distinct from cancelling input on blur or UI focus. */
  wasReleased(code:string):boolean{return this.released.has(code);}

  /** True if the mouse button went down since the last endTick() (mirrors wasPressed for keys). */
  wasButtonPressed(button: number): boolean {
    return this.pressedButtons.has(button);
  }

  /** Returns accumulated mouse delta since the last call, then resets it. */
  consumeMouse(): { dx: number; dy: number } {
    const out = { dx: this.mouseDX, dy: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return out;
  }

  /** Call once per sim tick after reading wasPressed(). */
  endTick(): void {
    this.pressed.clear();
    this.released.clear();
    this.pressedButtons.clear();
  }

  // --- DEV/test: drive input without real DOM events (headless harness). ---
  /** Hold/release a key (down also registers a one-tick wasPressed). */
  devKey(code: string, down: boolean): void {
    if (down) {
      this.keys.add(code);
      this.pressed.add(code);
    } else {
      if(this.keys.delete(code))this.released.add(code);
    }
  }
  /** Hold/release a mouse button (2 = right = aim). Down also registers a one-tick wasButtonPressed. */
  devButton(button: number, down: boolean): void {
    if (down) {
      this.buttons.add(button);
      this.pressedButtons.add(button);
    } else {
      this.buttons.delete(button);
    }
  }
  /** Inject a mouse-look delta (yaw via dx, pitch via dy) for the next tick. */
  devLook(dx: number, dy: number): void {
    this.mouseDX += dx;
    this.mouseDY += dy;
  }

  /** Optional controller haptics. This remains a no-op for keyboards and for
   * browsers/controllers without a vibration actuator. */
  rumble(strength: number, durationMs: number): void {
    const magnitude = Math.max(0, Math.min(1, strength));
    if (magnitude <= 0 || typeof navigator.getGamepads !== "function") return;
    type Haptic = { playEffect?: (type: string, params: {
      duration: number;
      startDelay: number;
      strongMagnitude: number;
      weakMagnitude: number;
    }) => Promise<unknown> };
    for (const gamepad of navigator.getGamepads()) {
      if (!gamepad?.connected) continue;
      const extended = gamepad as Gamepad & { vibrationActuator?: Haptic; hapticActuators?: Haptic[] };
      const actuator = extended.vibrationActuator ?? extended.hapticActuators?.[0];
      void actuator?.playEffect?.("dual-rumble", {
        duration: Math.max(20, durationMs),
        startDelay: 0,
        strongMagnitude: magnitude,
        weakMagnitude: Math.min(1, magnitude * 0.65),
      }).catch?.(() => {});
    }
  }
}
